/**
 * Fleet map — an SVG map of Australia with a marker per site, coloured by status and clickable
 * through to the site. Self-contained: the outlines and projection come from auMap.ts (no map tiles,
 * no API key), so it works on a locked-down / offline install. Sites without coordinates aren't
 * plotted (noted below the map).
 */
import { useNavigate } from 'react-router-dom';
import { AU_MAP, project } from './auMap';
import type { Site } from './api';

const [W, H] = AU_MAP.viewBox;

function markerColor(s: Site): string {
  if (!s.online) return 'var(--steel)';
  if (s.worst >= 4) return 'var(--crit)';
  if (s.worst >= 2) return 'var(--amber)';
  return 'var(--good)';
}
function statusText(s: Site): string {
  if (!s.online) return 'offline';
  if (s.active_alarms > 0)
    return `${s.active_alarms} active alarm${s.active_alarms === 1 ? '' : 's'}`;
  return 'OK';
}

export function FleetMap({ sites }: { sites: Site[] }): JSX.Element {
  const navigate = useNavigate();
  const located = sites.filter((s) => s.lat != null && s.lon != null);
  const missing = sites.length - located.length;

  return (
    <div className="panel fleetmap-card">
      <div className="fleetmap-head">
        <div className="section-label" style={{ margin: 0 }}>
          Fleet map
        </div>
        <div className="fleetmap-legend">
          <span>
            <i style={{ background: 'var(--good)' }} />
            OK
          </span>
          <span>
            <i style={{ background: 'var(--amber)' }} />
            Alarm
          </span>
          <span>
            <i style={{ background: 'var(--crit)' }} />
            Critical
          </span>
          <span>
            <i style={{ background: 'var(--steel)' }} />
            Offline
          </span>
        </div>
      </div>

      <div className="fleetmap">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Map of Australia showing site locations"
        >
          {AU_MAP.states.map((st) => (
            <path key={st.abbr} className="au-state" d={st.path} />
          ))}
          {AU_MAP.states.map((st) =>
            st.lx != null && st.ly != null ? (
              <text key={`l-${st.abbr}`} className="au-label" x={st.lx} y={st.ly}>
                {st.abbr}
              </text>
            ) : null,
          )}
          {located.map((s) => {
            const [x, y] = project(s.lat!, s.lon!);
            const color = markerColor(s);
            const attention = !s.online || s.worst >= 3;
            return (
              <g
                key={s.id}
                className="site-dot"
                onClick={() => navigate(`/sites/${s.id}`)}
                role="button"
                aria-label={`${s.name} — ${statusText(s)}`}
              >
                <title>{`${s.name} (${s.site_code}) — ${statusText(s)}`}</title>
                {attention && (
                  <circle className="site-halo" cx={x} cy={y} r={7} style={{ fill: color }} />
                )}
                <circle
                  cx={x}
                  cy={y}
                  r={7}
                  style={{ fill: color }}
                  stroke="rgba(0,0,0,.45)"
                  strokeWidth={1.5}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
            );
          })}
        </svg>
        {located.length === 0 && (
          <div className="fleetmap-empty">
            No site coordinates yet — add a lat/lon to a site to plot it here.
          </div>
        )}
      </div>

      {missing > 0 && located.length > 0 && (
        <div className="hint" style={{ marginTop: 8 }}>
          {missing} site{missing === 1 ? '' : 's'} without coordinates not shown.
        </div>
      )}
    </div>
  );
}
